import { useCallback, useEffect, useMemo } from 'react'

import {
  TypedDocumentNode,
  useQuery,
  useSubscription,
  gql
} from '@apollo/client'

// Helper type to extract the variables type from a query
type InferVariables<T> =
  T extends TypedDocumentNode<unknown, infer V> ? V : never

// Helper type to extract the query result type from a query
type InferQueryResult<T> =
  T extends TypedDocumentNode<infer R, unknown> ? R : never

// Helper type to get the first key of an object type
type FirstKey<T> = T extends object ? keyof T : never

// Helper type to ensure the object has exactly one key
type EnsureSingleKey<T> = T extends object
  ? keyof T extends FirstKey<T>
    ? FirstKey<T>
    : never
  : never

type ExtractItemType<T> = T extends {
  [K in keyof T]: {
    items: Array<infer I>
    metadata: { pageNumber: number; totalPages: number; totalItems: number }
  }
}[keyof T]
  ? I
  : never

// Helper type for the paginated response structure
interface PaginatedResponse<T> {
  metadata: {
    pageNumber: number
    pageSize: number
    totalPages: number
    totalEntries: number
  }
  items: T[]
}

interface SubscriptionResponse<T> {
  created: boolean
  deletedIds: number[]
  updated: T[]
}

const NOOP_SUBSCRIPTION = gql`
  subscription Noop {
    __typename
  }
`

const PAGE_SIZE = 25

export default function usePaginatedQuery<
  TQuery extends TypedDocumentNode,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  TSubscription extends TypedDocumentNode<unknown, any>
>(
  query: TQuery,
  variables: Omit<InferVariables<TQuery>, 'page' | 'pageSize'>,
  subscription?: {
    document: TSubscription
    typename: string
    variables?: InferVariables<TSubscription>
  }
) {
  type QueryResult = InferQueryResult<TQuery>
  type QueryName = EnsureSingleKey<QueryResult>
  type ItemType = ExtractItemType<QueryResult>

  const { loading, data, fetchMore, refetch } = useQuery<
    QueryResult,
    InferVariables<TQuery>
  >(query, {
    variables: {
      ...variables,
      page: 1,
      pageSize: PAGE_SIZE
    } as InferVariables<TQuery>,
    fetchPolicy: 'cache-and-network',
    refetchWritePolicy: 'overwrite'
  })

  const queryName = useMemo(() => {
    if (!data) return undefined
    return Object.keys(data)[0] as QueryName
  }, [data])

  const loadPage = useCallback(
    (page: number) => fetchMore({ variables: { page } }),
    [fetchMore]
  )

  useEffect(() => {
    refetch({ ...variables } as InferVariables<TQuery>)
  }, [variables, refetch])

  const paginatedResponse = useMemo(() => {
    if (!data || !queryName) return undefined

    return data[queryName] as PaginatedResponse<ItemType>
  }, [queryName, data])

  useSubscription<QueryResult>(subscription?.document ?? NOOP_SUBSCRIPTION, {
    skip: !subscription,
    variables: subscription?.variables,
    onData({ client, data: subData }) {
      if (!subscription || !subData?.data) return

      const subName = Object.keys(subData.data)[0] as QueryName
      const response = subData.data[subName] as SubscriptionResponse<ItemType>

      const hasCreated = response.created
      const deletedIds = response.deletedIds

      // Early return if nothing requires refetch (updates handled by Apollo automatically)
      if (!hasCreated && deletedIds.length === 0) return

      // Handle deletes - evict from cache
      if (deletedIds.length > 0) {
        deletedIds.forEach(id => {
          const identity = client.cache.identify({
            __typename: subscription.typename,
            id
          })

          client.cache.evict({ id: identity })
        })

        client.cache.gc()
      }

      if (paginatedResponse) {
        refetch({
          ...variables,
          page: paginatedResponse.metadata.pageNumber
        } as InferVariables<TQuery>)
      } else {
        refetch()
      }
    }
  })

  const items = useMemo(() => {
    if (!data || !queryName) return undefined

    const response = data[queryName] as PaginatedResponse<ItemType>
    const index = response.metadata.pageNumber - 1
    const start = index * PAGE_SIZE
    const end = start + PAGE_SIZE

    const items = response.items?.slice(start, end)

    return items
  }, [data, queryName])

  return {
    items,
    page: queryName
      ? (data?.[queryName] as PaginatedResponse<unknown>)?.metadata.pageNumber
      : undefined,
    metadata: queryName
      ? (data?.[queryName] as PaginatedResponse<unknown>)?.metadata
      : undefined,
    loading,
    loadPage
  }
}
